import { CountryCode, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { EXCLUDED_PHONE_COUNTRIES } from './constants';
import { extractPhoneDigits, normalizePhoneDigits } from './digits.util';
import { IParsePastedPhoneResult, ISavedPhoneNumberParseResult } from './types';

export function parseNationalPhoneNumber(country: CountryCode, digits: string) {
  if (!digits) {
    return undefined;
  }

  const callingCode = getCountryCallingCode(country);

  return parsePhoneNumberFromString(`+${callingCode}${digits}`, country);
}

export function isPhoneNumberValid(country: CountryCode, nationalDigits: string): boolean {
  const digits = normalizePhoneDigits(nationalDigits);

  if (!digits) {
    return false;
  }

  return parseNationalPhoneNumber(country, digits)?.isValid() ?? false;
}

export function toE164PhoneNumber(country: CountryCode, nationalDigits: string): string | null {
  const digits = normalizePhoneDigits(nationalDigits);
  const parsed = parseNationalPhoneNumber(country, digits);

  return parsed?.isValid() ? parsed.format('E.164') : null;
}

export function formatPhoneNumberForDisplay(phoneNumber: string): string {
  const trimmed = phoneNumber.trim();

  if (!trimmed) {
    return '';
  }

  const parsed = parsePhoneNumberFromString(trimmed);

  if (parsed?.isValid()) {
    return parsed.formatInternational();
  }

  return trimmed;
}

export function formatPhoneNumberNationalForDisplay(phoneNumber: string): string {
  const trimmed = phoneNumber.trim();

  if (!trimmed) {
    return '';
  }

  const parsed = parsePhoneNumberFromString(trimmed);

  if (parsed?.isValid()) {
    return parsed.formatNational().replace(/^0+/, '').trimStart();
  }

  return trimmed;
}

export function parsePastedPhoneValue(
  selectedCountry: CountryCode,
  pastedText: string
): IParsePastedPhoneResult {
  const trimmed = pastedText.trim();

  if (!trimmed) {
    return { country: selectedCountry, digits: '' };
  }

  const callingCode = getCountryCallingCode(selectedCountry);

  if (/^(\+|00)/.test(trimmed)) {
    const internationalText = trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;
    const parsed = parsePhoneNumberFromString(internationalText);

    if (
      parsed?.country &&
      parsed.nationalNumber &&
      !EXCLUDED_PHONE_COUNTRIES.includes(parsed.country)
    ) {
      return {
        country: parsed.country,
        digits: normalizePhoneDigits(parsed.nationalNumber),
      };
    }
  }

  const nationalParsed = parsePhoneNumberFromString(trimmed, selectedCountry);

  if (nationalParsed?.country === selectedCountry && nationalParsed.nationalNumber) {
    return {
      country: selectedCountry,
      digits: normalizePhoneDigits(nationalParsed.nationalNumber),
    };
  }

  let digits = extractPhoneDigits(trimmed);

  if (digits.startsWith(`00${callingCode}`)) {
    digits = digits.slice(2 + callingCode.length);
  } else if (digits.startsWith(callingCode)) {
    digits = digits.slice(callingCode.length);
  }

  return {
    country: selectedCountry,
    digits: normalizePhoneDigits(digits),
  };
}

export function parseSavedPhoneNumber(phoneNumber: string): ISavedPhoneNumberParseResult | null {
  const trimmed = phoneNumber.trim();

  if (!trimmed) {
    return null;
  }

  const parsed = parsePhoneNumberFromString(trimmed);

  if (!parsed?.isValid() || !parsed.country || !parsed.nationalNumber) {
    return null;
  }

  if (EXCLUDED_PHONE_COUNTRIES.includes(parsed.country)) {
    return null;
  }

  return {
    country: parsed.country,
    digits: normalizePhoneDigits(parsed.nationalNumber),
    e164: parsed.format('E.164'),
  };
}
